import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container", "content"];
  static values = {
    mode: String,
  };

  connect() {
    this.initialized = false;
    this.tryInitialize();
    this.setupResizeObserver();
    // Un morph (idiomorph : la bande d'un îlot après un broadcast, un frame refresh="morph")
    // ramène le contenu à son balisage serveur et efface les styles posés ici (taille,
    // marges, transform) : on remesure et on repose tout.
    document.addEventListener("turbo:morph-element", this._onMorph = (event) => this.handleMorph(event));
  }

  disconnect() {
    this.cleanupResizeObserver();
    document.removeEventListener("turbo:morph-element", this._onMorph);
  }

  handleMorph(event) {
    if (!(event.target instanceof Element) || !this.element.contains(event.target)) return;

    this.initialized = false;
    this.tryInitialize();
  }

  // === Initialization ===

  tryInitialize() {
    const containerRect = this.containerTarget.getBoundingClientRect();

    // Only initialize if container has valid dimensions
    if (containerRect.width > 0 || containerRect.height > 0) {
      if (!this.initialized) {
        this.initializeDimensions();
        this.setupContent();
        this.initialized = true;
      }
      this.applyScaling();
    }
  }

  initializeDimensions() {
    const firstChild = this.contentTarget.querySelector("[data-width]");
    if (!firstChild) return console.error("No first child found");

    this.originalWidth = parseInt(firstChild.dataset.width);
    if ("previewPhoto" in firstChild.dataset) this.matchCoverRatio(firstChild);

    const currentDimensions = firstChild.getBoundingClientRect();
    const currentRatio = currentDimensions.height / currentDimensions.width;
    this.originalHeight = currentRatio * this.originalWidth;
  }

  // Photo d'un carrousel (page sans template) : elle prend le ratio de la couverture rendue par
  // le template de la même version, sinon elle reste carrée.
  matchCoverRatio(photo) {
    const pages = this.element.closest('[data-controller~="media-template-pages"]');
    const cover = pages?.querySelector("[data-width]:not([data-preview-photo])");
    if (!cover) return;

    const rect = cover.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    photo.style.height = `${(rect.height / rect.width) * this.originalWidth}px`;
  }

  setupContent() {
    const { width, height } = this.originalDimensions;

    Object.assign(this.contentTarget.style, {
      width: `${width}px`,
      height: `${height}px`,
      marginLeft: `-${width / 2}px`,
      marginTop: `-${height / 2}px`,
      transformOrigin: "center",
    });
  }

  // === Scaling Logic ===

  applyScaling() {
    if (!this.initialized) return;

    const scalingMethod = this.isContainMode
      ? "applyContainScaling"
      : "applyCoverScaling";
    this[scalingMethod]();
  }

  applyCoverScaling() {
    const containerRect = this.containerTarget.getBoundingClientRect();
    const { width: containerWidth, height: containerHeight } = containerRect;

    const scaleX = containerWidth > 0 ? containerWidth / this.originalWidth : 0;
    const scaleY = containerHeight > 0 ? containerHeight / this.originalHeight : 0;

    let scale;
    if (scaleX === 0 && scaleY === 0) {
      scale = 1;
    } else if (scaleX === 0) {
      scale = scaleY;
    } else if (scaleY === 0) {
      scale = scaleX;
    } else {
      scale = Math.max(scaleX, scaleY);
    }

    this.setContentScale(scale);
  }

  applyContainScaling() {
    const containerRect = this.containerTarget.getBoundingClientRect();
    const { width: containerWidth, height: containerHeight } = containerRect;

    const scaleX = containerWidth > 0 ? containerWidth / this.originalWidth : Infinity;
    const scaleY = containerHeight > 0 ? containerHeight / this.originalHeight : Infinity;

    let scale;
    if (scaleX === Infinity && scaleY === Infinity) {
      scale = 1;
    } else if (scaleX === Infinity) {
      scale = scaleY;
    } else if (scaleY === Infinity) {
      scale = scaleX;
    } else {
      scale = Math.min(scaleX, scaleY);
    }

    // En mode contain, ajuster la hauteur ou la largeur du container
    if (containerHeight === 0 && containerWidth > 0) {
      const newHeight = (this.originalHeight / this.originalWidth) * containerWidth;
      this.containerTarget.style.height = `${newHeight}px`;
    } else if (containerHeight > 0 && containerWidth === 0) {
      const newWidth = (this.originalWidth / this.originalHeight) * containerHeight;
      this.containerTarget.style.width = `${newWidth}px`;
    }

    this.setContentScale(scale);
  }

  setContentScale(scale) {
    this.contentTarget.style.transform = `scale(${scale})`;
  }

  // === Event Handling ===

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.handleContainerResize();
    });

    this.resizeObserver.observe(this.containerTarget);
  }

  cleanupResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  handleContainerResize() {
    if (!this.initialized) {
      // Try to initialize if not yet done
      this.tryInitialize();
    } else {
      // Otherwise just reapply scaling
      this.applyScaling();
    }
  }

  // === Getters ===

  get originalDimensions() {
    return {
      width: this.originalWidth,
      height: this.originalHeight,
    };
  }

  get containerWidth() {
    return this.containerTarget.getBoundingClientRect().width;
  }

  get isContainMode() {
    return this.modeValue === "contain";
  }
}
